import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ITableFilterItem } from '../resources/interfaces';

interface UseTableFiltersOptions {
  initialFilters?: ITableFilterItem[];
  syncWithUrl?: boolean;
  onFiltersChange?: (filters: ITableFilterItem[]) => void;
}

export const useTableFilters = (options: UseTableFiltersOptions = {}) => {
  const { initialFilters = [], syncWithUrl = false, onFiltersChange } = options;
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState<ITableFilterItem[]>(initialFilters);

  // Sync filters with URL params
  useEffect(() => {
    if (syncWithUrl) {
      const urlFilters = filters.map(filter => ({
        ...filter,
        value: searchParams.get(filter.name) || filter.value
      }));
      setFilters(urlFilters);
    }
  }, []);

  // Update URL when filters change
  useEffect(() => {
    if (syncWithUrl) {
      const newSearchParams = new URLSearchParams(searchParams);
      
      filters.forEach(filter => {
        if (filter.value) {
          if (typeof filter.value === 'object' && filter.value.from) {
            // Handle date range
            if (filter.value.from) newSearchParams.set(`${filter.name}_from`, filter.value.from);
            if (filter.value.to) newSearchParams.set(`${filter.name}_to`, filter.value.to);
          } else {
            newSearchParams.set(filter.name, String(filter.value));
          }
        } else {
          // Remove filter param if value is empty
          newSearchParams.delete(filter.name);
          newSearchParams.delete(`${filter.name}_from`);
          newSearchParams.delete(`${filter.name}_to`);
        }
      });
      
      setSearchParams(newSearchParams);
    }
    
    onFiltersChange?.(filters);
  }, [filters]);

  // Utility functions for filter value extraction
  const getFilterValue = useMemo(() => (name: string) => {
    const filter = filters.find(f => f.name === name);
    return filter?.value;
  }, [filters]);

  const getFilterValues = useMemo(() => () => {
    return filters.reduce((acc, filter) => {
      acc[filter.name] = filter.value;
      return acc;
    }, {} as Record<string, any>);
  }, [filters]);

  const updateFilter = (name: string, value: any, value2?: string) => {
    setFilters(prevFilters => 
      prevFilters.map(filter => 
        filter.name === name 
          ? { ...filter, value, value2 }
          : filter
      )
    );
  };

  const updateFilterByIndex = (index: number, value: any, value2?: string) => {
    setFilters(prevFilters => {
      const newFilters = [...prevFilters];
      newFilters[index] = { ...newFilters[index], value, value2 };
      return newFilters;
    });
  };

  const resetFilters = (newFilters?: ITableFilterItem[]) => {
    setFilters(newFilters || initialFilters);
  };

  const hasActiveFilters = useMemo(() => {
    return filters.some(filter => {
      if (typeof filter.value === 'object' && filter.value !== null) {
        return filter.value.from || filter.value.to;
      }
      return filter.value !== '' && filter.value !== null && filter.value !== undefined;
    });
  }, [filters]);

  const getActiveFilters = useMemo(() => {
    return filters.filter(filter => {
      if (typeof filter.value === 'object' && filter.value !== null) {
        return filter.value.from || filter.value.to;
      }
      return filter.value !== '' && filter.value !== null && filter.value !== undefined;
    });
  }, [filters]);

  return {
    filters,
    setFilters,
    getFilterValue,
    getFilterValues,
    updateFilter,
    updateFilterByIndex,
    resetFilters,
    hasActiveFilters,
    getActiveFilters
  };
};
